import {createSelector} from 'reselect';

import {General, Preferences} from 'mattermost-redux/constants';

import {getConfig, getFeatureFlagValue, getLicense} from 'mattermost-redux/selectors/entities/general';

import {type PreferenceType} from 'mattermost-redux/types/preferences';
import {type GlobalState} from 'mattermost-redux/types/store';
import {type Theme} from 'mattermost-redux/types/themes';

import {createShallowSelector} from 'mattermost-redux/utils/helpers';
import {getPreferenceKey} from 'mattermost-redux/utils/preference_utils';
import {setThemeDefaults} from 'mattermost-redux/utils/theme_utils';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';

const CrtThreadPaneSteps = {
    THREADS_PANE_POPOVER: 0,
    FINISHED: 999,
};

const CrtTutorialSteps = {
    WELCOME_POPOVER: 0,
    FINISHED: 999,
};

const CrtTutorialTriggerSteps = {
    START: 0,
    FINISHED: 999,
};

const TutorialSteps = {
    ADD_FIRST_CHANNEL: -1,
    FINISHED: 999,
};

const AutoTourStatus = {
    ENABLED: 1,
    DISABLED: 0,
};

const AutoStatusSuffix = '_auto_tour_status';
const OnboardingTaskCategory = 'onboarding_task_list';

const TutorialTourName = {
    ONBOARDING_TUTORIAL_STEP: 'tutorial_step',
    CRT_TUTORIAL_STEP: 'crt_tutorial_step',
    CRT_THREAD_PANE_STEP: 'crt_thread_pane_step',
    AUTO_TOUR_STATUS: 'auto_tour_status',
};

const TTNameMapToATStatusKey = {
    [TutorialTourName.ONBOARDING_TUTORIAL_STEP]: TutorialTourName.ONBOARDING_TUTORIAL_STEP + AutoStatusSuffix,
    [TutorialTourName.CRT_TUTORIAL_STEP]: TutorialTourName.CRT_TUTORIAL_STEP + AutoStatusSuffix,
    [TutorialTourName.CRT_THREAD_PANE_STEP]: [TutorialTourName.CRT_THREAD_PANE_STEP] + AutoStatusSuffix,
    [TutorialTourName.AUTO_TOUR_STATUS]: [TutorialTourName.AUTO_TOUR_STATUS] + AutoStatusSuffix,
};

const GenericTaskSteps = {
    START: 0,
    FINISHED: 999,
};

export function getMyPreferences(state: GlobalState): {[x: string]: PreferenceType} {
    return state.entities.preferences.myPreferences;
}

export function get(state: GlobalState, category: string, name: string, defaultValue: any = '') {
    const key = getPreferenceKey(category, name);
    const prefs = getMyPreferences(state);

    if (!(key in prefs)) {
        return defaultValue;
    }

    return prefs[key].value;
}

export function getBool(state: GlobalState, category: string, name: string, defaultValue = false): boolean {
    const value = get(state, category, name, String(defaultValue));
    return value !== 'false';
}

export function getInt(state: GlobalState, category: string, name: string, defaultValue = 0): number {
    const value = get(state, category, name, defaultValue);
    return parseInt(value, 10);
}

export function makeGetCategory(): (state: GlobalState, category: string) => PreferenceType[] {
    return createSelector(
        getMyPreferences,
        (state: GlobalState, category: string) => category,
        (preferences, category) => {
            const prefix = category + '--';
            const prefsInCategory: PreferenceType[] = [];

            for (const key in preferences) {
                if (key.startsWith(prefix)) {
                    prefsInCategory.push(preferences[key]);
                }
            }

            return prefsInCategory;
        },
    );
}

const getDirectShowCategory = makeGetCategory();

export function getDirectShowPreferences(state: GlobalState) {
    return getDirectShowCategory(state, Preferences.CATEGORY_DIRECT_CHANNEL_SHOW);
}

const getGroupShowCategory = makeGetCategory();

export function getGroupShowPreferences(state: GlobalState) {
    return getGroupShowCategory(state, Preferences.CATEGORY_GROUP_CHANNEL_SHOW);
}

export const getTeammateNameDisplaySetting: (state: GlobalState) => string = createSelector(
    getConfig,
    getMyPreferences,
    getLicense,
    (config, preferences, license) => {
        const useAdminTeammateNameDisplaySetting =
            license && license.LockTeammateNameDisplay === 'true' && config.LockTeammateNameDisplay === 'true';
        const key = getPreferenceKey(Preferences.CATEGORY_DISPLAY_SETTINGS, Preferences.NAME_NAME_FORMAT);
        if (preferences[key] && !useAdminTeammateNameDisplaySetting) {
            return preferences[key].value || '';
        } else if (config?.TeammateNameDisplay) {
            return config.TeammateNameDisplay;
        }
        return General.TEAMMATE_NAME_DISPLAY.SHOW_USERNAME;
    },
);

const getThemePreference = createSelector(
    getMyPreferences,
    (state) => state.entities.teams.currentTeamId,
    (myPreferences, currentTeamId) => {
        // Prefer the user's current team-specific theme over the user's current global theme
        let themePreference;

        if (currentTeamId) {
            themePreference = myPreferences[getPreferenceKey(Preferences.CATEGORY_THEME, currentTeamId)];
        }

        if (!themePreference) {
            themePreference = myPreferences[getPreferenceKey(Preferences.CATEGORY_THEME, '')];
        }

        return themePreference;
    },
);

const getDefaultTheme = createSelector(getConfig, (config): Theme => {
    if (config.DefaultTheme && config.DefaultTheme in Preferences.THEMES) {
        const theme: Theme = Preferences.THEMES[config.DefaultTheme];
        if (theme) {
            return theme;
        }
    }

    // If no config.DefaultTheme or value doesn't refer to a valid theme name...
    return Preferences.THEMES.denim;
});

export const getTheme: (state: GlobalState) => Theme = createShallowSelector(
    getThemePreference,
    getDefaultTheme,
    (themePreference, defaultTheme): Theme => {
        const themeValue: Theme | string = themePreference?.value ?? defaultTheme;

        // A custom theme will be a JSON-serialized object stored in a preference
        // At this point, the theme should be a plain object
        const theme: Theme = typeof themeValue === 'string' ? JSON.parse(themeValue) : themeValue;

        return setThemeDefaults(theme);
    },
);

export function makeGetStyleFromTheme<Style>(): (
    state: GlobalState,
    getStyleFromTheme: (theme: Theme) => Style,
) => Style {
    return createSelector(
        getTheme,
        (state: GlobalState, getStyleFromTheme: (theme: Theme) => Style) => getStyleFromTheme,
        (theme, getStyleFromTheme) => {
            return getStyleFromTheme(theme);
        },
    );
}

// shouldShowUnreadsCategory returns true if the user has unereads grouped separately with the new sidebar enabled.
export const shouldShowUnreadsCategory: (state: GlobalState) => boolean = createSelector(
    (state: GlobalState) => get(state, Preferences.CATEGORY_SIDEBAR_SETTINGS, Preferences.SHOW_UNREAD_SECTION),
    (state: GlobalState) => get(state, Preferences.CATEGORY_SIDEBAR_SETTINGS, ''),
    (state: GlobalState) => getConfig(state)?.ExperimentalGroupUnreadChannels,
    (userPreference, oldUserPreference, serverDefault) => {
        // Prefer the show_unread_section user preference over the previous version
        if (userPreference) {
            return userPreference === 'true';
        }

        if (oldUserPreference) {
            return JSON.parse(oldUserPreference).unreads_at_top === 'true';
        }

        // The user setting is not set, so use the system default
        return serverDefault === General.DEFAULT_ON;
    },
);

export function shouldShowUnreadCollapsedChannels(state: GlobalState): boolean {
    return getBool(state, Preferences.CATEGORY_SIDEBAR_SETTINGS, Preferences.SHOW_UNREAD_COLLAPSED_CHANNELS, true);
}

export function shouldShowAvailabilityStatusOnPosts(): 'true' | 'false' {
    // return get(state, Preferences.CATEGORY_DISPLAY_SETTINGS, Preferences.AVAILABILITY_STATUS_ON_POSTS, Preferences.AVAILABILITY_STATUS_ON_POSTS_DEFAULT);
    return 'false';
}

export function shouldShowMilitaryTime(state: GlobalState): boolean {
    return getBool(state, Preferences.CATEGORY_DISPLAY_SETTINGS, Preferences.USE_MILITARY_TIME, false);
}

export function getChannelDisplayMode(
    state: GlobalState,
): typeof Preferences.CHANNEL_DISPLAY_MODE_CENTERED | typeof Preferences.CHANNEL_DISPLAY_MODE_FULL_SCREEN {
    return get(
        state,
        Preferences.CATEGORY_DISPLAY_SETTINGS,
        Preferences.CHANNEL_DISPLAY_MODE,
        Preferences.CHANNEL_DISPLAY_MODE_DEFAULT,
    );
}

export function getCollapsedThreadsPreference(): string {
    return Preferences.COLLAPSED_REPLY_THREADS_ON;
}

export function isCollapsedThreadsAllowed(state: GlobalState): boolean {
    return (
        getFeatureFlagValue(state, 'CollapsedThreads') === 'true' && getConfig(state).CollapsedThreads !== 'disabled'
    );
}

export function getChannelPinnedPostsEnabled(state: GlobalState): string {
    return get(
        state,
        Preferences.CATEGORY_DISPLAY_SETTINGS,
        Preferences.CHANNEL_PINNED_POSTS_ENABLED,
        Preferences.CHANNEL_PINNED_POSTS_ENABLED_DEFAULT,
    );
}

export function isChannelPinnedPostsEnabled(state: GlobalState): boolean {
    return getChannelPinnedPostsEnabled(state) !== 'false';
}

export function isCustomGroupsEnabled(state: GlobalState): boolean {
    return getFeatureFlagValue(state, 'CustomGroups') === 'true' && getConfig(state).EnableCustomGroups === 'true';
}

export function isNewProfileEnabled(state: GlobalState): boolean {
    return getConfig(state).EnableNewProfile === 'true';
}

export function isAnalyticsEnabled(state: GlobalState): boolean {
    return getConfig(state).AnalyticsEnabled === 'true';
}

export function getUseCaseOnboarding(state: GlobalState): boolean {
    return getFeatureFlagValue(state, 'UseCaseOnboarding') === 'true';
}

export function getTutorialStep(state: GlobalState): number {
    // TIME-505 Disable onboarding tours
    return TutorialSteps.FINISHED;
    const currentUserId = getCurrentUserId(state);
    return getInt(state, 'tutorial_step', currentUserId, TutorialSteps.ADD_FIRST_CHANNEL);
}

export function getCrtTutorialStep(state: GlobalState): number {
    // TIME-505 Disable onboarding tours
    return CrtTutorialSteps.FINISHED;
    const currentUserId = getCurrentUserId(state);
    return getInt(state, 'crt_tutorial_step', currentUserId, CrtTutorialSteps.WELCOME_POPOVER);
}

export function getAutoTourStep(state: GlobalState): number {
    // TIME-505 Disable onboarding tours
    return CrtTutorialSteps.FINISHED;
    const currentUserId = getCurrentUserId(state);
    return getInt(state, 'auto_tour_status', currentUserId, CrtTutorialSteps.WELCOME_POPOVER);
}

export function getCrtThreadPaneStep(state: GlobalState): number {
    // TIME-505 Disable onboarding tours
    return CrtThreadPaneSteps.FINISHED;
    const currentUserId = getCurrentUserId(state);
    return getInt(state, 'crt_thread_pane_step', currentUserId, CrtThreadPaneSteps.THREADS_PANE_POPOVER);
}

export function getCrtTutorialAutoTourStatus(state: GlobalState): number {
    // TIME-505 Disable onboarding tours
    return AutoTourStatus.DISABLED;
    const currentUserId = getCurrentUserId(state);
    return getInt(state, 'crt_tutorial_auto_tour_status', currentUserId, AutoTourStatus.ENABLED);
}

export function getAutoTourStatus(state: GlobalState, tourCategory: string): number {
    // TIME-505 Disable onboarding tours
    return AutoTourStatus.DISABLED;
    return getInt(state, tourCategory, TTNameMapToATStatusKey[tourCategory], AutoTourStatus.DISABLED);
}

export function getCrtTutorialTriggered(state: GlobalState): number {
    // TIME-505 Disable onboarding tours
    return CrtTutorialTriggerSteps.FINISHED;
    const currentUserId = getCurrentUserId(state);
    return getInt(state, 'crt_tutorial_triggered', currentUserId, CrtTutorialTriggerSteps.START);
}

export function getOnboardingTask(state: GlobalState, taskName: string, defaultValue = GenericTaskSteps.START) {
    // TIME-505 Disable onboarding tours
    return GenericTaskSteps.FINISHED;
    return getInt(state, OnboardingTaskCategory, taskName, defaultValue);
}

export function getActionsMenuTutorialState(state: GlobalState) {
    // TIME-505 Disable onboarding tours
    return JSON.stringify({[Preferences.ACTIONS_MENU_VIEWED]: true});
    return get(state, Preferences.CATEGORY_ACTIONS_MENU, Preferences.NAME_ACTIONS_MENU_TUTORIAL_STATE, false);
}

export function getCustomStatusTutorialState(state: GlobalState) {
    // TIME-505 Disable onboarding tours
    return JSON.stringify({[Preferences.CUSTOM_STATUS_MODAL_VIEWED]: true});
    return get(state, Preferences.CATEGORY_CUSTOM_STATUS, Preferences.NAME_CUSTOM_STATUS_TUTORIAL_STATE, false);
}
